/**
 * Payout API Contract Schemas
 *
 * Defines Zod schemas for payout-related API requests and responses.
 * Used for validation in both backend (NestJS) and frontend/mobile clients.
 *
 * IMPORTANT: TCS (Tax Collected at Source) is NOT applicable per Phase 3 CONTEXT.md.
 * Only TDS (Tax Deducted at Source) is calculated, and it is calculated on commission amount.
 */

import { z } from 'zod';

// ============================================
// ENUM SCHEMAS
// ============================================

/**
 * Payment mode enum for bank transfer method selection
 */
export const PaymentModeSchema = z.enum(['NEFT', 'IMPS', 'RTGS']);
export type PaymentMode = z.infer<typeof PaymentModeSchema>;

// ============================================
// REQUEST SCHEMAS
// ============================================

/**
 * Schema for creating a partial payout with manual amount
 */
export const CreatePartialPayoutSchema = z.object({
  vendorId: z.string().uuid('Invalid vendor ID format'),
  manualAmount: z
    .number()
    .positive('Manual amount must be greater than 0')
    .finite('Manual amount must be a finite number'),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
  immediateProcess: z.boolean().default(false),
  paymentMode: PaymentModeSchema.optional().default('NEFT'),
});
export type CreatePartialPayoutDto = z.infer<typeof CreatePartialPayoutSchema>;

/**
 * Schema for processing a payout immediately
 */
export const ProcessPayoutImmediateSchema = z.object({
  payoutId: z.string().uuid('Invalid payout ID format'),
});
export type ProcessPayoutImmediateDto = z.infer<typeof ProcessPayoutImmediateSchema>;

// ============================================
// RESPONSE SCHEMAS
// ============================================

/**
 * Payout status enum
 * Must match backend Prisma schema enum PayoutStatus
 */
export const PayoutStatusSchema = z.enum([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'RETRY_PENDING',
  'EXHAUSTED',
  'HELD',
]);
export type PayoutStatus = z.infer<typeof PayoutStatusSchema>;

/**
 * Payout calculation breakdown schema
 *
 * Formula (per CONTEXT.md):
 * - Gross Amount = Service Price (from completed bookings)
 * - Platform Commission = Gross × Commission Rate (from admin config)
 * - TDS = Platform Commission × TDS Rate (from admin settings)
 * - Net Payout = Gross - Platform Commission - TDS
 *
 * NOTE: TCS is NOT applicable and has been removed.
 */
export const PayoutBreakdownSchema = z.object({
  grossAmount: z.number().nonnegative(),
  commissionRate: z.number().nonnegative(),
  commissionAmount: z.number().nonnegative(),
  gstAmount: z.number().nonnegative().optional().default(0),
  gstRate: z.number().nonnegative().optional().default(0),
  tdsRate: z.number().nonnegative(),
  tdsAmount: z.number().nonnegative(),
  netPayoutAmount: z.number().nonnegative(),
  totalAdminCouponDiscount: z.number().nonnegative().optional().default(0),
  totalVendorDiscount: z.number().optional(),
});
export type PayoutBreakdown = z.infer<typeof PayoutBreakdownSchema>;

/**
 * Vendor payout summary schema for vendor dashboard
 */
export const VendorPayoutSummarySchema = z.object({
  pendingAmount: z.number().nonnegative(),
  lastPayoutAmount: z.number().nullable(),
  lastPayoutDate: z.string().datetime().nullable(),
  totalPaidYTD: z.number().nonnegative(),
  nextPayoutDate: z.string().datetime().nullable(),
  breakdown: PayoutBreakdownSchema,
});
export type VendorPayoutSummary = z.infer<typeof VendorPayoutSummarySchema>;

/**
 * Payout history item schema for list views
 * Updated to include vendorName and hasBankAccount for improved UX
 */
export const PayoutHistoryItemSchema = z.object({
  id: z.string().uuid(),
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  amount: z.number().nonnegative(),
  status: PayoutStatusSchema,
  periodStart: z.string(), // ISO date format (YYYY-MM-DD)
  periodEnd: z.string(), // ISO date format (YYYY-MM-DD)
  hasBankAccount: z.boolean(), // Warning indicator for missing bank details
  processedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  bankAccountLast4: z.string().nullable().optional(), // Deprecated, keeping for backward compatibility
  breakdown: PayoutBreakdownSchema.optional(),
});
export type PayoutHistoryItem = z.infer<typeof PayoutHistoryItemSchema>;

/**
 * Schema for eligible vendor data (admin use)
 * NOTE: TCS removed per Phase 3 decisions
 */
export const EligibleVendorSchema = z.object({
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  vendorEmail: z.string().email(),
  isVerified: z.boolean(),
  eligibleBookingCount: z.number().int().nonnegative(),
  grossAmount: z.number().nonnegative(),
  platformFeeAmount: z.number().nonnegative(),
  tdsAmount: z.number().nonnegative(),
  netPayoutAmount: z.number().nonnegative(),
  previousRemainingBalance: z.number().nonnegative(),
  totalPayable: z.number().nonnegative(),
  bookingIds: z.array(z.string().uuid()),
});
export type EligibleVendor = z.infer<typeof EligibleVendorSchema>;

/**
 * Schema for generate daily payouts result
 */
export const GenerateDailyPayoutsResultSchema = z.object({
  vendorsProcessed: z.number().int().nonnegative(),
  payoutsCreated: z.number().int().nonnegative(),
  totalAmount: z.number().nonnegative(),
  errors: z.array(
    z.object({
      vendorId: z.string().uuid(),
      error: z.string(),
    })
  ),
});
export type GenerateDailyPayoutsResult = z.infer<typeof GenerateDailyPayoutsResultSchema>;

/**
 * Schema for admin payout list response with pagination metadata
 * Used by admin dashboard to display paginated payout history
 */
export const AdminPayoutListResponseSchema = z.object({
  data: z.array(PayoutHistoryItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type AdminPayoutListResponse = z.infer<typeof AdminPayoutListResponseSchema>;

/**
 * Schema for payout statement with GST breakdown
 * Includes CGST/SGST for same state or IGST for different state
 */
export const PayoutStatementSchema = z.object({
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  summary: z.object({
    totalGross: z.number().nonnegative(),
    totalCommission: z.number().nonnegative(),
    totalTds: z.number().nonnegative(),
    totalGst: z.number().nonnegative(), // GST on commission (service tax)
    cgst: z.number().nonnegative(), // CGST component (if same state)
    sgst: z.number().nonnegative(), // SGST component (if same state)
    igst: z.number().nonnegative(), // IGST component (if different state)
    totalNet: z.number().nonnegative(),
  }),
  bookings: z.array(
    z.object({
      id: z.string().uuid(),
      date: z.string().datetime(),
      customerName: z.string(), // Masked for privacy
      venueName: z.string(),
      grossAmount: z.number().nonnegative(),
      commission: z.number().nonnegative(),
      tds: z.number().nonnegative(),
      gst: z.number().nonnegative(),
      net: z.number().nonnegative(),
    })
  ),
  payouts: z.array(
    z.object({
      id: z.string().uuid(),
      date: z.string().datetime(),
      amount: z.number().nonnegative(),
      status: z.string(),
      bankAccountLast4: z.string().nullable(),
    })
  ),
});
export type PayoutStatement = z.infer<typeof PayoutStatementSchema>;

// ─── Phase 2: Admin Diagnostics ─────────────────────────────────────────────

export const VendorEligibilityDiagnosticSchema = z.object({
  vendorId: z.string(),
  vendorName: z.string(),
  vendorEmail: z.string(),
  isVerified: z.boolean(),
  hasBankAccount: z.boolean(),
  totalBookingCount: z.number(),
  eligibleBookingCount: z.number(),
  excludedBreakdown: z.object({
    paymentNotCompleted: z.number(),
    alreadyInPayout: z.number(),
    walkIn: z.number(),
    notCommenced: z.number(),
  }),
  grossAmount: z.number(),
  netPayable: z.number(),
  minThreshold: z.number(),
  isEligible: z.boolean(),
  ineligibilityReasons: z.array(z.string()),
  blockedByPayoutIds: z.array(z.string()),
});
export type VendorEligibilityDiagnostic = z.infer<typeof VendorEligibilityDiagnosticSchema>;

export const EligibilityDiagnosticsResponseSchema = z.object({
  vendors: z.array(VendorEligibilityDiagnosticSchema),
  systemSettings: z.object({
    minThreshold: z.number(),
    tdsEnabled: z.boolean(),
    tcsEnabled: z.boolean(),
    gstRate: z.number(),
    eligibilityCriteria: z.string(),
  }),
  summary: z.object({
    totalVendors: z.number(),
    eligibleVendors: z.number(),
    ineligibleVendors: z.number(),
  }),
});
export type EligibilityDiagnosticsResponse = z.infer<typeof EligibilityDiagnosticsResponseSchema>;

export const ReleaseBookingsResponseSchema = z.object({
  success: z.boolean(),
  payoutId: z.string(),
  previousStatus: z.string(),
  releasedBookingCount: z.number(),
});
export type ReleaseBookingsResponse = z.infer<typeof ReleaseBookingsResponseSchema>;

export const PayoutHealthCheckSchema = z.object({
  lastCronRunAt: z.string().nullable(),
  hoursSinceLastRun: z.number().nullable(),
  cronStatus: z.enum(['healthy', 'stale', 'unknown']),
  stalePendingPayouts: z.number(),
  staleProcessingPayouts: z.number(),
  missingPayoutItemCount: z.number(),
  totalHeldPayouts: z.number(),
  totalRetryPendingPayouts: z.number(),
  checkedAt: z.string(),
});
export type PayoutHealthCheck = z.infer<typeof PayoutHealthCheckSchema>;

// ─── Phase 3: Admin Revenue Transparency ────────────────────────────────────

export const RevenuePeriodSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  commissionEarned: z.number(),
  gstCollected: z.number(),
  tdsWithheld: z.number(),
  netPlatformRevenue: z.number(),
  totalDisbursed: z.number(),
  payoutCount: z.number(),
});
export type RevenuePeriod = z.infer<typeof RevenuePeriodSchema>;

export const TopVendorByCommissionSchema = z.object({
  vendorId: z.string(),
  vendorName: z.string(),
  commissionGenerated: z.number(),
  grossBookingAmount: z.number(),
  payoutCount: z.number(),
});
export type TopVendorByCommission = z.infer<typeof TopVendorByCommissionSchema>;

export const RevenueSummaryResponseSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  totals: z.object({
    commissionEarned: z.number(),
    gstCollected: z.number(),
    tdsWithheld: z.number(),
    netPlatformRevenue: z.number(),
    totalDisbursedToVendors: z.number(),
    pendingDisbursement: z.number(),
    completedPayoutCount: z.number(),
  }),
  trend: z.array(RevenuePeriodSchema),
  topVendors: z.array(TopVendorByCommissionSchema),
});
export type RevenueSummaryResponse = z.infer<typeof RevenueSummaryResponseSchema>;

export const RevenueForecastResponseSchema = z.object({
  forecastedCommission: z.number(),
  forecastedGst: z.number(),
  forecastedNetRevenue: z.number(),
  eligibleBookingCount: z.number(),
  eligibleGrossAmount: z.number(),
  commissionRate: z.number(),
  gstRate: z.number(),
  asOf: z.string(),
});
export type RevenueForecastResponse = z.infer<typeof RevenueForecastResponseSchema>;

// ─── Phase 4: Vendor Earnings Transparency ──────────────────────────────────

export const PayoutBookingItemSchema = z.object({
  bookingId: z.string(),
  bookingReference: z.string().optional(),
  venueName: z.string(),
  bookingDate: z.string(),
  bookingAmount: z.number(),
  platformFeeAmount: z.number(),
  platformFeePercentage: z.number(),
  gstAmount: z.number(),
  tdsAmount: z.number(),
  netAmount: z.number(),
});
export type PayoutBookingItem = z.infer<typeof PayoutBookingItemSchema>;

export const PayoutBookingsResponseSchema = z.object({
  payoutId: z.string(),
  items: z.array(PayoutBookingItemSchema),
  total: z.number(),
});
export type PayoutBookingsResponse = z.infer<typeof PayoutBookingsResponseSchema>;

export const VendorEligibilityStatusSchema = z.object({
  isVerified: z.boolean(),
  hasBankAccount: z.boolean(),
  pendingEligibleAmount: z.number(),
  eligibleBookingCount: z.number(),
  blockedPayouts: z.array(z.object({ payoutId: z.string(), status: z.string() })),
  nextPayoutDate: z.string().nullable(),
  statusMessage: z.string(),
  canReceivePayouts: z.boolean(),
});
export type VendorEligibilityStatus = z.infer<typeof VendorEligibilityStatusSchema>;

export const VendorPendingBookingSchema = z.object({
  bookingId: z.string(),
  bookingReference: z.string().optional(),
  venueName: z.string(),
  bookingDate: z.string(),
  grossAmount: z.number(),
  expectedCommission: z.number(),
  expectedTds: z.number(),
  expectedNetPayout: z.number(),
  status: z.string(),
  paymentStatus: z.string(),
});
export type VendorPendingBooking = z.infer<typeof VendorPendingBookingSchema>;

export const VendorPendingBookingsResponseSchema = z.object({
  bookings: z.array(VendorPendingBookingSchema),
  totalGrossAmount: z.number(),
  totalExpectedPayout: z.number(),
  count: z.number(),
});
export type VendorPendingBookingsResponse = z.infer<typeof VendorPendingBookingsResponseSchema>;

// ─── Phase 6: Enhanced Admin Analytics ──────────────────────────────────────

export const OrphanedBookingSchema = z.object({
  bookingId: z.string(),
  vendorId: z.string(),
  vendorName: z.string(),
  venueName: z.string(),
  bookingDate: z.string(),
  grossAmount: z.number(),
  status: z.string(),
  paymentStatus: z.string(),
  reason: z.string(), // why it fell through
});
export type OrphanedBooking = z.infer<typeof OrphanedBookingSchema>;

export const BookingReconciliationResponseSchema = z.object({
  totalEligibleBookings: z.number(),
  bookingsWithPayouts: z.number(),
  orphanedBookings: z.number(),
  totalOrphanedAmount: z.number(),
  orphanedBookingsList: z.array(OrphanedBookingSchema),
  asOf: z.string(),
});
export type BookingReconciliationResponse = z.infer<typeof BookingReconciliationResponseSchema>;

export const VendorFinancialSummaryItemSchema = z.object({
  vendorId: z.string(),
  vendorName: z.string(),
  vendorEmail: z.string(),
  totalGrossAmount: z.number(),
  totalCommissionDeducted: z.number(),
  totalGstDeducted: z.number(),
  totalTdsWithheld: z.number(),
  totalPaidToVendor: z.number(),
  pendingPayoutAmount: z.number(),
  completedPayoutCount: z.number(),
  pendingPayoutCount: z.number(),
});
export type VendorFinancialSummaryItem = z.infer<typeof VendorFinancialSummaryItemSchema>;

export const VendorFinancialSummaryResponseSchema = z.object({
  vendors: z.array(VendorFinancialSummaryItemSchema),
  platformTotals: z.object({
    totalGrossAmount: z.number(),
    totalCommissionEarned: z.number(),
    totalGstCollected: z.number(),
    totalTdsWithheld: z.number(),
    totalDisbursed: z.number(),
    totalPending: z.number(),
  }),
  vendorCount: z.number(),
  asOf: z.string(),
});
export type VendorFinancialSummaryResponse = z.infer<typeof VendorFinancialSummaryResponseSchema>;
